/**
 * 全站共享常量：枚举口径、品牌文案、公开资源地址。
 * 列筛选（valueEnum）、表单 Select（options）、详情展示三端共用同一处定义，避免口径分裂。
 */

/** 后端 API 统一前缀（开发期由 .umirc.ts 代理到 8088） */
export const API_BASE = '/api';

/**
 * 写操作鉴权 Bearer Token 占位。
 * - 为空串时不附加 Authorization 头；
 * - 后端 ENFORCE_WRITE_AUTH=false 时无 token 也能跑通；
 * - 启用鉴权后在此填入正式 token（src/app.ts 的 request 拦截器统一附加）。
 */
export const WRITE_TOKEN = '5f682c14dcf342e2a3f40e7be31fe61ec6d83ef76e1146daa6ea8ae146c1fb38';

/**
 * 后台管理员登录令牌（localStorage 键）
 * 登录页写入；request 拦截器优先使用它调业务接口， WRITE_TOKEN 仅作部署期兜底。
 */
export const ADMIN_TOKEN_KEY = 'trace_admin_token';

// ---------------- 品牌与公开资源 ----------------

/** 平台名 */
export const BRAND_NAME = '极宴食品溯源平台';

/** 扫码页页脚固定文案 */
export const BRAND_FOOTER = `由${BRAND_NAME}提供技术支持`;

/** 扫码落地页地址（产品溯源码 code 对外唯一标识） */
export const scanUrl = (code: string) => `/s/${code}`;

/**
 * 二维码图片地址（后端直接输出 PNG，禁止前端 base64 自绘）
 * ------------------------------------------------------------------
 * `v` 为二维码缓存版本号：二维码内容由后端 BASE_PUBLIC_URL 决定，
 * 一旦对外入口地址变更（例如更换域名/端口），必须把此版本号 +1，
 * 否则浏览器会继续命中旧缓存，页面上仍显示指向老地址的二维码。
 * 变更历史：v1 = jycfoods.cn:8443（被微信拦截）；v2 = s.jycfoods.cn（标准 443）
 */
export const QR_CACHE_VERSION = '2';
export const qrcodeImgUrl = (code: string) =>
  `${API_BASE}/public/qrcode/${code}.png?v=${QR_CACHE_VERSION}`;

// ---------------- 枚举 ----------------

/** 企业主体类型（五枚举），列筛选 valueEnum 与表单 Select 共用 */
export const ORG_TYPES = ['生产', '加工', '仓储', '物流', '销售'] as const;

/** 企业类型列筛选/详情展示用 valueEnum */
export const ORG_TYPE_ENUM: Record<string, { text: string }> = ORG_TYPES.reduce(
  (acc, t) => {
    acc[t] = { text: t };
    return acc;
  },
  {} as Record<string, { text: string }>,
);

/** 企业类型表单下拉 options */
export const ORG_TYPE_OPTIONS = ORG_TYPES.map((t) => ({ label: t, value: t }));

/** 批次状态：valueEnum + Tag 颜色映射共用一处 */
export const BATCH_STATUS_ENUM: Record<string, { text: string; color: string }> = {
  created: { text: '新建', color: 'default' },
  active: { text: '进行中', color: 'processing' },
  archived: { text: '归档', color: 'gold' },
};

/** 批次状态表单下拉 options */
export const BATCH_STATUS_OPTIONS = Object.entries(BATCH_STATUS_ENUM).map(
  ([value, v]) => ({ label: v.text, value }),
);

/** 溯源环节六枚举（records 表单约束 + 公开扫码页图标映射共用键名） */
export const STEP_OPTIONS = ['生产', '加工', '质检', '仓储', '物流', '销售'] as const;
